import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import {
  BasketDelivery,
  BasketDetails,
  BasketDiscount,
  BasketItem,
  BasketTax,
} from '../../../transactions-layout/models';

/**
 * TODO: bring in line with the legacy transaction detail page basket (transaction-detail-page-legacy):
 *  - discounts shown as negative amounts
 *  - per-line tax amount / rate column and line totals from Thor's `total`
 *  - delivery and basket-level tax rows with their VAT breakdown
 *  - invoice shows customer and merchant tax numbers, dates without time
 *
 * Basket & invoice display. Two modes:
 *  - full table when line items carry SKUs / unit prices (e.g. LTI payments)
 *  - simple "qty × description … price" rows for minimal baskets
 * Always offers the raw basket JSON as a collapsed escape hatch.
 */
@Component({
  standalone: false,
  selector: 'odin-basket-card',
  templateUrl: './basket-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './basket-card.component.scss',
})
export class BasketCardComponent {
  @Input({ required: true }) basket: BasketDetails;
  /** currency code for the currency pipe, e.g. 'GBP' */
  @Input({ required: true }) currency: string;
  /** the transaction amount — shown as the reconciling total */
  @Input({ required: true }) chargedAmount: number;

  public get items(): BasketItem[] {
    return (this.basket.basketItems || []).filter((i) => i !== null && i !== undefined);
  }

  public get discounts(): BasketDiscount[] {
    return (this.basket.basketDiscounts || []).filter((d) => d !== null && d !== undefined);
  }

  public get taxes(): BasketTax[] {
    return (this.basket.basketTaxes || []).filter((t) => t !== null && t !== undefined);
  }

  public get delivery(): BasketDelivery | null {
    return this.basket.delivery ?? null;
  }

  public get itemCount(): number {
    return this.items.reduce((sum, item) => sum + (item.quantity || 1), 0);
  }

  /** full table only when the data justifies the columns */
  public get isRichBasket(): boolean {
    return this.items.some((i) => !!i.sku || !!i.unitPrice);
  }

  /** basket amounts arrive in major units (e.g. 12.00 = £12.00), as stored by Thor */
  public unitPrice(item: BasketItem): number {
    return item.unitPrice ?? 0;
  }

  /** Thor's line total already accounts for any line discount; fall back to qty × unit price */
  public lineTotal(item: BasketItem): number {
    return item.total ?? (item.unitPrice ?? 0) * (item.quantity || 1);
  }

  public get subtotal(): number {
    return this.items.reduce((sum, item) => sum + this.lineTotal(item), 0);
  }

  public get rawBasketJson(): string {
    return JSON.stringify(this.basket, null, 2);
  }
}
